<ng-template #orderDetails let-modal>
	<div class="modal-header">
		<h4 class="modal-title" id="modal-title" *ngIf="podetails">
			<a [routerLink]="'/vendors/'+podetails.poheader.supplierno">{{ podetails.poheader.supplierno }}</a> Purchase Order
		</h4>
		<button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')">
			<i class="fa fa-close"></i>
		</button>
	</div>
	<div class="modal-body">
		<mat-tab-group #tabPanel mat-align-tabs="start" color="accent" class="no-overflow">
			<!-- Purchase Order Tab -->
			<mat-tab label="Purchase Order">
				<div class="po-details" *ngIf="podetails">
					<!-- PO Header -->
					<h4 class="mt-4 mb-0"><strong>Purchase Order #{{ podetails.poheader.orderno }}</strong></h4>

					<div *ngIf="tpl3plPgsEnabled" class="mt-3 mb-3" style="border-left:4px solid #1976d2;padding:12px 16px;background:#f5f9ff;border-radius:4px;">
						<h6 class="mb-2">
							<mat-icon style="vertical-align:middle;font-size:18px;color:#1976d2;">local_shipping</mat-icon>
							PGS 360 Inbound
						</h6>
						<p class="text-muted mb-2" style="font-size:0.82rem;">
							Notify PGS to expect this purchase order before goods arrive at the warehouse.
						</p>
						<div *ngIf="inboundStatusLoading" class="text-muted small">Loading inbound status…</div>
						<ng-container *ngIf="!inboundStatusLoading">
							<div class="mb-2" style="font-size:0.82rem;">
								<span class="badge" [ngClass]="{
									'badge-success': getInboundPanelDisplay().state === 'sent' || getInboundPanelDisplay().state === 'received',
									'badge-info': getInboundPanelDisplay().state === 'receiving',
									'badge-danger': getInboundPanelDisplay().state === 'error',
									'badge-secondary': getInboundPanelDisplay().state === 'not_sent'
								}">
									{{ getInboundPanelDisplay().label }}
								</span>
								<span *ngIf="inboundStatus?.pgsInboundID" class="text-muted ml-2">PGS ID: {{ inboundStatus.pgsInboundID }}</span>
								<span *ngIf="inboundStatus?.attempt_count" class="text-muted ml-2">{{ inboundStatus.attempt_count }} attempt(s)</span>
							</div>
							<div *ngIf="inboundLastMessage" class="text-muted small mb-2" style="word-break:break-word;">{{ inboundLastMessage }}</div>
							<div class="d-flex flex-wrap" style="gap:8px;">
								<button mat-raised-button color="primary" [disabled]="inboundSending"
									(click)="sendInboundToPgs(podetails.poheader.orderno, inboundAlreadySent)">
									{{ inboundSending ? 'Sending…' : (inboundAlreadySent ? 'Re-send to PGS' : 'Send to PGS') }}
								</button>
								<button mat-stroked-button color="primary" [disabled]="inboundSending"
									(click)="openInboundPreview(podetails.poheader.orderno, inboundAlreadySent)">
									<mat-icon>preview</mat-icon>
									Preview &amp; Send
								</button>
							</div>
						</ng-container>
					</div>

					<!-- Contact and Delivery Information Cards -->
					<div class="row m-0 p-0">
						<!-- Contact Information -->
						<div class="col-md-6">
							<div class="card shadow-sm mb-4">
								<div class="card-header bg-primary text-white">
									<strong>Contact Information</strong>
								</div>
								<div class="card-body">
									<p>
										<strong>Contact:</strong> {{ podetails.poheader.contact }}<br>
										<strong>Address:</strong>
										<ng-container *ngIf="podetails.address">
											<div [innerHTML]="podetails.address"></div>
										</ng-container>
									</p>
								</div>
							</div>
						</div>

						<!-- Delivery Information -->
						<div class="col-md-6">
							<div class="card shadow-sm mb-4">
								<div class="card-header bg-primary text-white">
									<strong>Delivery Information</strong>
								</div>
								<div class="card-body">
									<p>
										<strong>Delivery Address:</strong><br>
										<ng-container *ngFor="let field of ['deladd1', 'deladd2', 'deladd3', 'deladd4', 'deladd5']">
											<span *ngIf="podetails.poheader[field]">{{ podetails.poheader[field] }}<br></span>
										</ng-container>
									</p>
								</div>
							</div>
						</div>
					</div>

					<!-- Notes and Totals -->
					<div class="row m-0 p-0">
						<!-- Notes Section -->
						<div class="col-md-6">
							<div class="card shadow-sm mb-4">
								<div class="card-header bg-info text-white">
									<strong>Notes</strong>
								</div>
								<div class="card-body">
									<div [innerHTML]="podetails.poheader.stat_comment"></div>
								</div>
							</div>
						</div>

						<!-- Totals Section -->
						<div class="col-md-6">
							<div class="card shadow-sm mb-4">
								<div class="card-header bg-info text-white">
									<strong>Totals</strong>
								</div>
								<div class="card-body">
									<p>
										<strong>Total Quantity:</strong> {{ getTotalQuantity() }}<br>
										<span *ngIf="config.env.package === 'tires'">
											<strong>FET Total:</strong> {{ getTotalFet() | currency }}<br>
										</span>
										<strong>Total Value:</strong> {{ getTotalValueWithFet() | currency }}
									</p>
								</div>
							</div>
						</div>
					</div>

					<!-- Purchase Order Lines Table -->
					<div class="table-responsive mt-3">
						<table class="table table-sm table-striped table-hover table-bordered">
							<thead class="thead-dark">
								<tr>
									<th>ITEM</th>
									<th>DESCRIPTION</th>
									<th>COMMENT</th>
									<th class="text-center">QTY</th>
									<th class="text-center">RCVD</th>
									<th class="text-center">PENDING</th>
									<th class="text-right">PRICE</th>
									<th class="text-right" *ngIf="config.env.package === 'tires'">FET</th>
									<th class="text-right">TOTAL</th>
								</tr>
							</thead>
							<tbody>
							  <tr *ngFor="let item of podetails.polines">
								<td>
								  <button mat-icon-button (click)="copyToClipboard(item.itemcode)" title="Copy to clipboard" class="mr-2">
									<mat-icon>content_copy</mat-icon>
								  </button>
								  <a [routerLink]="'/inventory/view/' + item.itemcode" (click)="modal.dismiss('route change')" class="font-weight-bold">
									{{ item.itemcode }}
								  </a>
								</td>
								<td>{{ item.itemdescription }}</td>
								<td class="font-weight-lighter">{{item.lineitemtext}}</td>
								<td class="text-center">{{ item.quantityord }}</td>
								<td class="text-center">{{ item.quantityrecd }}</td>
								<td class="text-center">{{ item.quantityord - item.quantityrecd }}</td>
								<td class="text-right">{{ item.unitprice | currency }}</td>
								<td class="text-right" *ngIf="config.env.package === 'tires'">{{ item.fetcharge | currency }}</td>
								<td class="text-right">{{ getLineTotal(item) | currency }}</td>
							  </tr>
							</tbody>

							<tfoot class="bg-light font-weight-bold">
							  <tr>
								<td colspan="3">Total</td>
								<td class="text-center">{{ getTotalQuantity() }}</td>
								<td [attr.colspan]="config.env.package === 'tires' ? 3 : 4"></td>
								<ng-container *ngIf="config.env.package === 'tires'">
									<td class="text-right">FET Total: {{ getTotalFet() | currency }}</td>
									<td></td>
								</ng-container>
							  </tr>
							  <tr>
								<td [attr.colspan]="config.env.package === 'tires' ? 8 : 7" class="text-right">Grand Total:</td>
								<td class="text-right">{{ getTotalValueWithFet() | currency }}</td>
							  </tr>
							</tfoot>



						</table>
					</div>

				</div>
			</mat-tab>

			<!-- Receiving Details Tab -->
			<mat-tab label="Receiving Details">
				<ng-container *ngIf="podetails.receiving.length">
					<table class="table table-fixed table-sm">
						<thead>
							<tr>
								<th>GRN</th>
								<th>Order#</th>
								<th>Item#</th>
								<th>Description</th>
								<th>Received</th>
								<th>Invoiced</th>
								<th>Date</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let r of podetails.receiving">
								<td><a class="btn btn-primary" href="{{config.apiServer.baseUrl}}{{grnlink}}{{r.grnbatch}}">{{ r.grnno }}</a></td>
								<td>{{ r.poorderno }}</td>
								<td>{{ r.itemcode }}</td>
								<td>{{ r.itemdescription }}</td>
								<td>{{ r.qtyrecd }}</td>
								<td>{{ r.quantityinv }}</td>
								<td>{{ r.update_date }}</td>
							</tr>
						</tbody>
					</table>
				</ng-container>
				<ng-container *ngIf="!podetails.receiving.length">
					<p>No Receiving Details</p>
				</ng-container>
			</mat-tab>
			<!-- Receiving Movements Tab -->
			<mat-tab label="Receiving Movements">
				<ng-container *ngIf="podetails.movements.length">
					<table class="table table-fixed table-sm">
						<thead>
							<tr>
								<th>Item#</th>
								<th>Location</th>
								<th>User</th>
								<th>Quantity</th>
								<th>QOH</th>
								<th>Date</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let r of podetails.movements">
								<td>{{ r.stockid }}</td>
								<td>{{ r.loccode }}</td>
								<td>{{ r.userid }}</td>
								<td>{{ r.qty }}</td>
								<td>{{ r.newqoh }}</td>
								<td>{{ r.trandate }}</td>
							</tr>
						</tbody>
					</table>
				</ng-container>
				<ng-container *ngIf="!podetails.movements.length">
					<p>No Movements</p>
				</ng-container>
			</mat-tab>
		</mat-tab-group>
	</div>
	<div class="modal-footer">
		<button type="button" class="btn btn-secondary" (click)="modal.dismiss('cancel')">Close</button>
	</div>
</ng-template>



<div class="opo-wrap" [class.opo-vendor]="embedded" [ngStyle]="themeVars">

	<div class="opo-icon-badge" *ngIf="!embedded">
		<i class="material-icons">shopping_cart</i>
	</div>

	<div class="opo-header">
		<div class="opo-title-group">
			<span class="opo-title">Open Purchase Orders</span>
			<span class="opo-subtitle" *ngIf="statCount">{{ statCount }} order{{ statCount === 1 ? '' : 's' }}</span>
		</div>
		<div class="opo-actions">
			<button class="opo-icon-btn" type="button" (click)="exportXls()" [disabled]="!purchaseorders" title="Export Excel">
				<i class="fa fa-file-excel-o"></i>
			</button>
			<button class="opo-icon-btn" type="button" (click)="exportPdf()" [disabled]="!purchaseorders" title="Export PDF">
				<i class="fa fa-file-pdf-o"></i>
			</button>
			<button class="opo-icon-btn" type="button" (click)="loadData()" [disabled]="loading" title="Refresh">
				<i class="material-icons">refresh</i>
			</button>
			<button class="opo-icon-btn"
					type="button"
					[class.opo-icon-btn--active]="showFilters"
					(click)="showFilters = !showFilters"
					title="Filters">
				<i class="material-icons">tune</i>
			</button>
		</div>
	</div>

	<div class="opo-stats" *ngIf="statCount && !loading">
		<div class="opo-stat">
			<span class="opo-stat-val">{{ statCount }}</span>
			<span class="opo-stat-lbl">Open POs</span>
		</div>
		<div class="opo-stat">
			<span class="opo-stat-val">{{ statTotalValue | currency }}</span>
			<span class="opo-stat-lbl">Total Value</span>
		</div>
		<div class="opo-stat">
			<span class="opo-stat-val">{{ statAvgValue | currency }}</span>
			<span class="opo-stat-lbl">Avg Order</span>
		</div>
	</div>

	<div class="opo-filters" *ngIf="showFilters">
		<mat-form-field appearance="outline" class="opo-field" *ngIf="statuses">
			<mat-label>Status</mat-label>
			<mat-select [formControl]="status">
				<mat-option [value]="''" (click)="onStatusSelection()">All</mat-option>
				<mat-option [value]="'OPEN'" (click)="onStatusSelection()">Open</mat-option>
				<mat-option *ngFor="let v of statuses" [value]="v.status_id" (click)="onStatusSelection()">
					{{ v.name }}
				</mat-option>
			</mat-select>
		</mat-form-field>

		<div class="opo-field opo-field--toggle" *ngIf="config.env.package === 'tires'">
			<mat-slide-toggle (change)="switchToSize()">Raw Size?</mat-slide-toggle>
		</div>

		<mat-form-field appearance="outline" class="opo-field" *ngIf="!searchBySize">
			<mat-label>Item Search</mat-label>
			<input matInput placeholder="Enter item keyword" [formControl]="searchInputItems" (input)="searchItems()" class="text-right">
			<span matSuffix>
				<button mat-icon-button (click)="searchItems()">
					<mat-icon>search</mat-icon>
				</button>
			</span>
		</mat-form-field>

		<mat-form-field appearance="outline" class="opo-field" *ngIf="searchBySize">
			<mat-label>Size Search</mat-label>
			<input matInput placeholder="Enter raw size" [formControl]="searchInputItems" (input)="searchSize()" class="text-right">
			<span matSuffix>
				<button mat-icon-button (click)="searchSize()">
					<mat-icon>search</mat-icon>
				</button>
			</span>
		</mat-form-field>

		<mat-form-field appearance="outline" class="opo-field opo-field--wide">
			<mat-label>Search</mat-label>
			<input tabindex="1" placeholder="Order no, supplier, date, item, requisition, initiator"
				   matInput (input)="onInput()" [formControl]="vendorSearch" class="text-right" autofocus
				   (keydown.enter)="$event.preventDefault(); onInput()">
			<span matSuffix>
				<button tabindex="-1" mat-icon-button>
					<mat-icon (click)="searchOrders()">search</mat-icon>
				</button>
			</span>
			<mat-progress-bar mode="indeterminate" color="accent" *ngIf="searching"></mat-progress-bar>
		</mat-form-field>
	</div>

	<div class="opo-loading" *ngIf="loading">
		<svg class="spinner" width="40" height="40" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
			<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
		</svg>
	</div>

	<div class="opo-empty" *ngIf="!loading && !statCount">No open purchase orders</div>

	<div class="opo-body" *ngIf="!loading">
		<div class="opo-table-wrap po-rga-mat-table-scroll" [class.d-none]="!statCount">
			<table mat-table [dataSource]="purchaseorders" matSort (matSortChange)="announceSortChange($event)" class="opo-table open-po-mat-table">
		<ng-container matColumnDef="actions">
			<th mat-header-cell *matHeaderCellDef mat-sort-header></th>
			<td mat-cell *matCellDef="let tran">
				<ng-container *ngIf="updatingStatusOrderNo === tran.orderno; else actionsMenu">
					<mat-spinner diameter="24" class="d-inline-block align-middle"></mat-spinner>
				</ng-container>
				<ng-template #actionsMenu>
				<button mat-icon-button [matMenuTriggerFor]="menu" aria-label="Actions">
					<mat-icon>more_vert</mat-icon>
				</button>
				<mat-menu #menu="matMenu">
					<button mat-menu-item (click)="viewOrder(tran.orderno, orderDetails)">
						<mat-icon>visibility</mat-icon>
						<span>View</span>
					</button>
					<a href="{{config.apiServer.baseUrl}}{{baselink}}{{tran.orderno}}" mat-menu-item>
						<mat-icon>print</mat-icon>
						<span>PDF</span>
					</a>
					<a href="{{config.apiServer.baseUrl}}{{baselink}}{{tran.orderno}}&outstanding=1" mat-menu-item>
						<mat-icon>print</mat-icon>
						<span>PDF (outstanding)</span>
					</a>
					<a href="{{config.apiServer.baseUrl}}{{explink}}{{tran.orderno}}" mat-menu-item>
						<mat-icon>save</mat-icon>
						<span>Excel</span>
					</a>
					<a href="{{config.apiServer.baseUrl}}{{reclink}}{{tran.orderno}}" mat-menu-item>
						<mat-icon>print</mat-icon>
						<span>Rcv Doc</span>
					</a>
					<mat-divider></mat-divider>
					<a [routerLink]="'/purchasing/receive/'+tran.orderno" mat-menu-item>
						<mat-icon>call_received</mat-icon>
						<span>Receive</span>
					</a>
					<ng-container *ngIf="tpl3plPgsEnabled">
						<mat-divider></mat-divider>
						<button mat-menu-item (click)="sendInboundToPgs(tran.orderno, false)">
							<mat-icon>local_shipping</mat-icon>
							<span>Send inbound to PGS</span>
						</button>
						<button mat-menu-item (click)="openInboundPreview(tran.orderno, false)">
							<mat-icon>preview</mat-icon>
							<span>Preview inbound send</span>
						</button>
					</ng-container>
					<mat-divider></mat-divider>
					<button mat-menu-item (click)="loadOrder(tran.orderno)">
						<mat-icon>edit</mat-icon>
						<span>Edit PO</span>
					</button>
				</mat-menu>
				</ng-template>
			</td>
		</ng-container>
		<ng-container matColumnDef="status_id">
			<th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
			<td mat-cell *matCellDef="let tran">
				<ng-container *ngIf="updatingStatusOrderNo === tran.orderno; else statusContent">
					<mat-spinner diameter="24" class="d-inline-block align-middle"></mat-spinner>
					<span class="align-middle ms-2 text-muted">Updating...</span>
				</ng-container>
				<ng-template #statusContent>
				<ng-container *ngIf="!tran.editing">
					<span class="order-status-pill order-status-pill--row status-{{ tran.status_id }}" [ngStyle]="tran | statusBadgeNgStyle">
						<span class="order-status-pill__start"></span>
						<span class="order-status-pill__text">{{ tran.statusname }}</span>
						<span class="order-status-pill__end">
							<i class="fa fa-edit order-status-pill__edit" (click)="toggleStatusChange(tran); $event.stopPropagation()"></i>
						</span>
					</span>
				</ng-container>
					<mat-form-field appearance="outline" *ngIf="tran.editing" [ngClass]="{'show-warning': tran.editing && orderwarning && orderwarning.orderno == tran.orderno}">
						<mat-label>Status</mat-label>
						<span matPrefix *ngIf="tran.editing">
							<mat-icon (click)="toggleStatusChange(tran)" class="smaller">clear</mat-icon>
						</span>
						<mat-select [(value)]="tran.status_id" (selectionChange)="updateStatus($event, tran)">
							<mat-option *ngFor="let status of statuses" [(value)]="status.status_id">{{ status.name }}</mat-option>
						</mat-select>
						<mat-hint class="hide-by-default text-danger">&nbsp;{{orderwarning.msg}}</mat-hint>
					</mat-form-field>
				</ng-template>
			</td>
		</ng-container>
		<ng-container matColumnDef="tpl3pl_inbound">
			<th mat-header-cell *matHeaderCellDef mat-sort-header>3PL Inbound</th>
			<td mat-cell *matCellDef="let tran">
				<ng-container *ngIf="getTpl3plInboundStatus(tran) as tpl3plSt">
					<span class="badge"
						[ngClass]="{
							'badge-success': tpl3plSt.state === 'sent' || tpl3plSt.state === 'received',
							'badge-info': tpl3plSt.state === 'receiving',
							'badge-danger': tpl3plSt.state === 'error',
							'badge-secondary': tpl3plSt.state === 'not_sent'
						}"
						[matTooltip]="tpl3plSt.detail"
						matTooltipPosition="above">
						{{ tpl3plSt.label }}
					</span>
				</ng-container>
			</td>
		</ng-container>
		<ng-container matColumnDef="orderno">
			<th mat-header-cell *matHeaderCellDef mat-sort-header>PO#</th>
			<td mat-cell *matCellDef="let tran">{{ tran.orderno }}</td>
		</ng-container>
		<ng-container matColumnDef="orddate">
			<th mat-header-cell *matHeaderCellDef mat-sort-header> Date Ordered</th>
			<td mat-cell *matCellDef="let tran">{{ tran.orddate }}</td>
		</ng-container>
		<ng-container matColumnDef="suppname">
			<th mat-header-cell *matHeaderCellDef mat-sort-header>Vendor</th>
			<td mat-cell *matCellDef="let tran">
				{{ tran.suppname }}
			</td>
		</ng-container>
		<ng-container matColumnDef="comments">
			<th mat-header-cell *matHeaderCellDef mat-sort-header>Comments</th>
			<td mat-cell *matCellDef="let tran">
				<div [innerHTML]="tran.comments" class="comments"></div>
			</td>
		</ng-container>
		<ng-container matColumnDef="requisitionno">
			<th mat-header-cell *matHeaderCellDef mat-sort-header>Req#</th>
			<td mat-cell *matCellDef="let tran">{{ tran.requisitionno }}</td>
		</ng-container>

		<ng-container matColumnDef="ordervalue">
			<th mat-header-cell *matHeaderCellDef mat-sort-header>Net $</th>
			<td mat-cell *matCellDef="let tran">{{ tran.ordervalue | currency }}</td>
		</ng-container>
		<ng-container matColumnDef="orderpendingvalue">
			<th mat-header-cell *matHeaderCellDef mat-sort-header>Pending $</th>
			<td mat-cell *matCellDef="let tran">{{ tran.orderpendingvalue | currency }}</td>
		</ng-container>
		<ng-container matColumnDef="ordereditems">
			<th mat-header-cell *matHeaderCellDef mat-sort-header>ORD/REC/PEND</th>
			<td mat-cell *matCellDef="let tran">{{ tran.ordered }}/{{ tran.rec }}/<b>{{ tran.pendingitems }}</b></td>
		</ng-container>
		<ng-container matColumnDef="deliverydate">
			<th mat-header-cell *matHeaderCellDef mat-sort-header arrowPosition="before" class="text-right">Date Wanted</th>
			<td mat-cell *matCellDef="let tran" class="text-right">{{ tran.deliverydate }}</td>
		</ng-container>
		<ng-container matColumnDef="initiator">
			<th mat-header-cell *matHeaderCellDef mat-sort-header class="text-right" arrowPosition="before"> Initiator</th>

			<td mat-cell *matCellDef="let tran" class="text-right">
				{{ tran.initiator }}
			</td>
		</ng-container>
		<tr mat-header-row *matHeaderRowDef="headercolumns;sticky: true;"></tr>
		<tr mat-row *matRowDef="let row; columns: headercolumns"></tr>
		<tr mat-row *matNoDataRow>
			<td class="mat-cell text-center" [attr.colspan]="headercolumns.length">No open purchase orders</td>
		</tr>
	</table>

			<mat-paginator [pageSizeOptions]="[50, 100, 500]" showFirstLastButtons aria-label="select page">
			</mat-paginator>
		</div>
	</div>
</div>